import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence } from 'framer-motion';

// 🔥 GOOGLE GİRİŞİ İÇİN GEREKLİ 🔥
import { GoogleOAuthProvider } from '@react-oauth/google';

// 🔥 BİLDİRİMLER İÇİN GEREKLİ 🔥
import { Toaster } from './components/ui/toaster'; 

// --- CONTEXT PROVIDERS ---
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { FoxProvider } from './context/FoxContext';
import { ModalProvider, useModals } from './context/ModalContext';

// --- GLOBAL BİLEŞENLER ---
import LoginScreen from './pages/LoginScreen'; // Sayfa klasöründen
import FoxChatOverlay from './components/FoxChatOverlay';
import LegalDisclaimerModal from './components/LegalDisclaimerModal';
import CookieBanner from './components/CookieBanner'; 
import PageTransition from './components/PageTransition'; // ✨ YENİ: Geçiş Animasyonları

// --- MODALLAR ---
import PremiumModal from './components/PremiumModal';
import ScanModal from './components/ScanModal';
import EditDocumentModal from './components/EditDocumentModal';

// --- LAYOUT ---
import Layout from './components/Layout'; 

// --- SAYFALAR ---
import HomePage from './pages/HomePage';
import Dashboard from './pages/Dashboard';
import ArchivePage from './pages/ArchivePage';
import ProfilePage from './pages/ProfilePage';
import AnalysisResultPage from './pages/AnalysisResultPage';
import CreateInvoicePage from './pages/CreateInvoicePage';
import TemplatesPage from './pages/TemplatesPage';
import SalaryCalculatorPage from './pages/SalaryCalculatorPage';
import AcademyPage from './pages/AcademyPage';
import OfficialProcedures from './pages/OfficialProcedures';
import SubscriptionPage from './pages/SubscriptionPage';
import SettingsPage from './pages/SettingsPage';
import ImpressumPage from './pages/ImpressumPage';
import PrivacyPage from './pages/PrivacyPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import NotFoundPage from './pages/NotFoundPage';

// ⚠️ Güvenlik: .env dosyasından çekilmesi önerilir
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "178954887274-h2fdu0l0348eqmhjag3otsr5abfrs1ek.apps.googleusercontent.com";

// --- BOOTING SCREEN (Korumalı Rota) ---
const ProtectedRoute = ({ children }) => {
  const { user, isGuest, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#f8fafc] dark:bg-[#020617] overflow-hidden relative transition-colors duration-500">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-500/10 rounded-full blur-[100px]" />
        <div className="text-center space-y-8 relative z-10">
          <div className="relative w-20 h-20 mx-auto">
              <div className="absolute inset-0 border-t-2 border-orange-500 border-r-2 border-r-transparent rounded-full animate-[spin_1s_linear_infinite]"></div>
              <div className="absolute inset-3 border-b-2 border-emerald-400 border-l-2 border-l-transparent rounded-full animate-[spin_2s_linear_infinite_reverse]"></div>
              <div className="absolute inset-[38%] bg-slate-900 dark:bg-white rounded-full animate-pulse shadow-[0_0_15px_rgba(255,255,255,0.5)]"></div>
          </div>
          <div className="space-y-2">
            <p className="text-slate-900 dark:text-white font-black uppercase tracking-[0.3em] text-xs">Fox Terminal</p>
            <p className="text-slate-500 dark:text-slate-400 font-mono text-[10px] animate-pulse">System Initializing...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (!user && !isGuest) return <Navigate to="/login" replace />;
  return children;
};

// --- MODAL YÖNETİCİSİ ---
const ModalTerminal = () => {
  const { activeModal, closeModal, modalData } = useModals();
  return (
    <>
      <PremiumModal isOpen={activeModal === 'premium'} onClose={closeModal} />
      <ScanModal isOpen={activeModal === 'scan'} onClose={closeModal} />
      {activeModal === 'edit' && (
        <EditDocumentModal 
          isOpen={true} onClose={closeModal} 
          document={modalData?.document} onUpdateSuccess={modalData?.onUpdateSuccess} 
        />
      )}
    </>
  );
};

function AppContent() {
  const location = useLocation();
  
  return (
    <div className="App relative min-h-screen bg-[#f8fafc] dark:bg-[#020617] transition-colors duration-500 flex flex-col font-sans overflow-x-hidden selection:bg-orange-500/30">
      <LegalDisclaimerModal />
      <CookieBanner />
      <ModalTerminal />
      
      {/* Sayfa Geçiş Animasyonları için AnimatePresence */}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          
          {/* PUBLIC ROUTES - Animasyonsuz veya Basit Geçiş */}
          <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
          <Route path="/login" element={<PageTransition><LoginScreen /></PageTransition>} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/impressum" element={<PageTransition><ImpressumPage /></PageTransition>} />
          <Route path="/privacy" element={<PageTransition><PrivacyPage /></PageTransition>} />

          {/* PROTECTED ROUTES - Layout İçinde */}
          <Route path="/*" element={
            <ProtectedRoute>
              <Layout>
                <AnimatePresence mode="wait">
                  <Routes location={location} key={location.pathname}>
                    <Route path="/dashboard" element={<PageTransition><Dashboard /></PageTransition>} />
                    <Route path="/archive" element={<PageTransition><ArchivePage /></PageTransition>} />
                    <Route path="/profile" element={<PageTransition><ProfilePage /></PageTransition>} />
                    <Route path="/settings" element={<PageTransition><SettingsPage /></PageTransition>} />
                    <Route path="/analysis-result" element={<PageTransition><AnalysisResultPage /></PageTransition>} />
                    <Route path="/create-invoice" element={<PageTransition><CreateInvoicePage /></PageTransition>} />
                    <Route path="/templates" element={<PageTransition><TemplatesPage /></PageTransition>} />
                    <Route path="/salary-calculator" element={<PageTransition><SalaryCalculatorPage /></PageTransition>} />
                    <Route path="/academy" element={<PageTransition><AcademyPage /></PageTransition>} />
                    <Route path="/official-procedures" element={<PageTransition><OfficialProcedures /></PageTransition>} />
                    <Route path="/subscription" element={<PageTransition><SubscriptionPage /></PageTransition>} />
                    <Route path="*" element={<PageTransition><NotFoundPage /></PageTransition>} />
                  </Routes>
                </AnimatePresence>
              </Layout>
            </ProtectedRoute>
          } />

        </Routes>
      </AnimatePresence>

      <FoxChatOverlay />
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <HelmetProvider>
        <LanguageProvider>
          <ThemeProvider>
            <AuthProvider>
              <ModalProvider>
                <FoxProvider>
                  <BrowserRouter>
                    <AppContent />
                  </BrowserRouter>
                </FoxProvider>
              </ModalProvider>
            </AuthProvider>
          </ThemeProvider>
        </LanguageProvider>
      </HelmetProvider>
    </GoogleOAuthProvider>
  );
}

export default App;